import {
    Phone,
    ArrowUpRight,
    Check,
    SquareDashed,
    PaintRoller,
    Sparkles,
    Ruler,
    CalendarCheck,
    ShieldCheck,
    Brush,
    HelpCircle,
    Home as HomeIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

const SERVICES = [
    {
        key: "drywall",
        icon: SquareDashed,
        eyebrow: "01",
        title: "Drywall Repair & Patching",
        lede: "From dings to holes the size of a fist — patched, sanded, finished, and ready to paint.",
        body:
            "Whether it's a doorknob ding, a crack from settling, water damage, or a hole the dog put through the wall — we cut, patch, tape, mud, sand, and feather it in so the repair disappears. Ceilings, walls, corners, around outlets and switches.",
        bullets: [
            "Small holes & cracks",
            "Large patch repairs",
            "Water-damage repairs",
            "Corner bead replacement",
            "Ceiling patches",
            "Patches around outlets & fixtures",
        ],
    },
    {
        key: "texture",
        icon: Sparkles,
        eyebrow: "02",
        title: "Texture Matching & Popcorn Ceiling Removal",
        lede: "Smooth finish or matched original texture — including popcorn ceiling removal done right.",
        body:
            "We match knockdown, orange peel, splatter, skip trowel, and Spanish lace so patches blend invisibly. Want a modern smooth-wall look? We scrape and retexture popcorn ceilings cleanly — containment first, dust contained, surfaces protected, ceiling finished and ready for paint.",
        bullets: [
            "Knockdown texture",
            "Orange peel texture",
            "Splatter & skip trowel",
            "Popcorn ceiling removal",
            "Patch-to-existing texture match",
            "Smooth-wall (Level 5) finish",
        ],
    },
    {
        key: "paint",
        icon: PaintRoller,
        eyebrow: "03",
        title: "Interior & Exterior Painting",
        lede: "Prep, prime, cut-in, two-coat finish. Inside or outside. We don't cut corners on prep.",
        body:
            "A great paint job is 70% prep. We mask, drop, prime where needed, cut in clean lines by hand, and roll out a true two-coat finish. Inside walls and ceilings, trim and cabinets, exterior siding, touch-ups, color matching — we'll leave it sharp and the job site cleaner than we found it.",
        bullets: [
            "Interior walls & ceilings",
            "Trim, doors & cabinets",
            "Exterior siding & trim",
            "Touch-up & color matching",
            "Stain-blocking primer",
            "Drop cloths & full prep",
        ],
    },
];

const PROCESS = [
    { i: HelpCircle, t: "Free estimate", d: "We come look, measure, and give you a clear written quote." },
    { i: CalendarCheck, t: "Schedule", d: "Pick a day that works. Most jobs scheduled within a week." },
    { i: ShieldCheck, t: "Prep & protect", d: "Drop cloths, plastic, dust containment — your stuff stays clean." },
    { i: Brush, t: "Repair, texture, paint", d: "We do the work. You either watch or come back to a finished room." },
    { i: Sparkles, t: "Cleanup", d: "Trash hauled, surfaces wiped, walkthrough — we leave it cleaner than we found it." },
];

const STATS = [
    { n: "25+", l: "Years on the wall" },
    { n: "Free", l: "Written estimates" },
    { n: "1", l: "Day for most repairs" },
];

export default function DrywallPaint() {
    useSeo({
        title: "Drywall Repair, Texture Match & Interior/Exterior Painting · Medford, Oregon | 5 Star Construction",
        description: "Drywall repair, texture matching, popcorn ceiling removal, and interior/exterior painting in Medford and Southern Oregon. Manufactured-home interior finishing specialist. 25+ years on the wall. Licensed Oregon contractor (CCB #232477). Free estimates — 541-292-9620.",
        path: "/drywall-paint",
        jsonLdKey: "drywall-paint",
        jsonLd: [
            serviceSchema({
                name: "Drywall Repair, Texture Matching & Painting",
                description: "Drywall patching, crack and hole repair, texture matching (knockdown, orange peel, splatter), popcorn ceiling removal, and interior/exterior painting in Medford and the Rogue Valley.",
                path: "/drywall-paint",
                serviceType: "Drywall & Painting Contractor",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Drywall & Paint", path: "/drywall-paint" },
            ]),
        ],
    });
    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="inline-flex items-center gap-2 mb-6 border border-[#E11D2A] px-3 py-1.5">
                        <span className="w-1.5 h-1.5 bg-[#E11D2A]" />
                        <span className="font-['Manrope'] text-xs font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Drywall · Texture · Paint</span>
                    </div>
                    <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-7xl lg:text-8xl uppercase max-w-5xl">
                        Patch it. <span className="text-[#FFD400]">Texture it.</span>
                        <br />
                        <span className="text-[#E11D2A]">Paint it right.</span>
                    </h1>
                    <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-3xl">
                        Drywall repair, texture match, popcorn ceiling removal, and interior/exterior painting — all by one crew that&apos;s been on the wall for 25 years. Honest prep, clean lines, no surprises on the invoice.
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-4">
                        <a href={SITE.phoneHref} data-testid="dp-call-cta">
                            <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" data-testid="dp-quote-cta">
                            <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A FREE QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>

                    <div className="mt-14 grid sm:grid-cols-3 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {STATS.map((s) => (
                            <div key={s.l} className="bg-[#111111] p-7">
                                <div className="font-['Anton'] text-5xl text-[#E11D2A] tracking-tight">{s.n}</div>
                                <div className="font-['Manrope'] text-xs uppercase tracking-[0.2em] font-bold mt-2 text-[#F6F5F2]/80">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service blocks */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-14">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">What we handle</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                Three services. One clean finish.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Most patching jobs roll right into texture matching, then paint — so you only deal with one crew and one quote. We can do any one of these on its own, or all three end-to-end.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {SERVICES.map(({ icon: Icon, ...s }) => (
                            <article key={s.key} className="grid md:grid-cols-12 gap-6 md:gap-10 bg-white border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all p-6 md:p-10" data-testid={`dp-service-${s.key}`}>
                                <div className="md:col-span-4 lg:col-span-3 flex md:block items-center gap-4">
                                    <div className="w-14 h-14 md:w-20 md:h-20 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center shrink-0">
                                        <Icon className="w-7 h-7 md:w-10 md:h-10" strokeWidth={2.25} />
                                    </div>
                                    <div className="md:mt-6">
                                        <div className="font-['Anton'] text-3xl md:text-4xl text-[#E11D2A] tracking-tight">{s.eyebrow}</div>
                                        <h3 className="mt-1 font-display font-extrabold tracking-tight text-[#111812] text-2xl md:text-3xl lg:text-4xl leading-[0.95]">
                                            {s.title}
                                        </h3>
                                    </div>
                                </div>
                                <div className="md:col-span-8 lg:col-span-9">
                                    <p className="font-['Manrope'] text-base md:text-lg font-bold text-[#E11D2A]">{s.lede}</p>
                                    <p className="mt-3 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">{s.body}</p>
                                    <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 pt-6 border-t border-[#E2DFD8]">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2 text-sm md:text-base font-semibold text-[#111812]">
                                                <Check className="w-4 h-4 text-[#E11D2A] mt-1 shrink-0" strokeWidth={3} />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

                        {/* Manufactured Home Interior Close-Ups — featured callout */}            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 mb-5 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <HomeIcon className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Our specialty</span>
                        </div>
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.92] text-4xl sm:text-5xl lg:text-6xl">
                            Manufactured Home
                            <br />
                            <span className="text-[#FFD400]">Interior Close-Ups.</span>
                            <br />
                            <span className="text-[#E11D2A]">Full Interiors.</span>
                        </h2>
                        <p className="mt-6 font-['Manrope'] text-lg md:text-xl text-[#F6F5F2]/80 max-w-2xl leading-relaxed">
                            New manufactured-home setups, remodels, and full interior finish work — taped seams, textured walls, doors, trim, and detail finishing. We&apos;re set up with all major retailers and have 25 years on the wall.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="dp-mh-call">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="dp-mh-quote">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    REQUEST A QUOTE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <ul className="lg:col-span-5 grid grid-cols-1 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {[
                            { t: "Full interior finishing", d: "Tape, texture, and paint complete on new setups." },
                            { t: "Doors & detail work", d: "Hung, trimmed, hardware installed, finished clean." },
                            { t: "All brands · all retailers", d: "We've worked with every major retailer in the region." },
                            { t: "25 years experience", d: "Set up to handle volume or one-off interiors." },
                        ].map((b) => (
                            <li key={b.t} className="bg-[#111111] p-6">
                                <div className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-[#FFD400] mt-1 shrink-0" strokeWidth={3} />
                                    <div>
                                        <div className="font-display text-lg font-extrabold text-[#F6F5F2]">{b.t}</div>
                                        <div className="font-['Manrope'] text-sm text-[#F6F5F2]/70 mt-1">{b.d}</div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Process */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="text-center mb-14">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">How it goes</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl md:text-6xl">
                            From estimate to clean walls — five steps.
                        </h2>
                    </div>
                    <ol className="grid md:grid-cols-5 gap-px bg-[#E2DFD8] border border-[#E2DFD8]">
                        {PROCESS.map(({ i: I, t, d }, idx) => (
                            <li key={t} className="bg-white p-6 md:p-7" data-testid={`dp-process-${idx}`}>
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-12 h-12 bg-[#111111] text-[#FFD400] flex items-center justify-center">
                                        <I className="w-5 h-5" strokeWidth={2.25} />
                                    </div>
                                    <span className="font-['Anton'] text-3xl text-[#E11D2A] tracking-tight">0{idx + 1}</span>
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-y-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Got a hole, a popcorn ceiling, or a paint job?
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free estimates. Honest pricing. Real Oregon contractor (CCB #232477) — call or send a request and we&apos;ll get back to you the same day.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="dp-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="dp-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
